/**
 * Role-appropriate landing routes and navigation for the SRS personas.
 *
 * This module controls presentation only. The API remains the authorization
 * boundary and verifies every permission independently.
 */
import { rolesForUser } from "./roles";

export const PERSONA_NAV_ITEMS = {
  dashboard: { to: "/dashboard", label: "Status Dashboard", permission: "dashboard.view" },
  clients: { to: "/clients", label: "Clients", permission: "client.manage" },
  orders: { to: "/orders", label: "Orders", permission: "order.manage" },
  "register-sample": {
    to: "/samples?register=1",
    label: "Samples & Registration",
    permission: "sample.register",
  },
  samples: { to: "/samples", label: "Sample Status", permission: "sample.view" },
  storage: { to: "/storage", label: "Storage", permission: "storage.manage" },
  catalogue: { to: "/catalogue", label: "Test Catalog", permission: "catalogue.manage" },
  worklist: {
    to: "/my-work",
    label: "My Work",
    permission: "result.enter",
    // FR-08: assignment changes surface as an unread badge here.
    badge: "notifications",
  },
  "my-stats": { to: "/my-stats", label: "My Stats", permission: "result.enter" },
  schedule: { to: "/schedule", label: "Schedule & Capacity", permission: "test.assign" },
  "supervisor-review": {
    to: "/review",
    label: "Supervisor Review",
    permission: "result.review",
    // Issue #94: submitted work waiting for first-level review lands here.
    badge: "notifications",
  },
  "qa-release": {
    to: "/review",
    label: "QA Release",
    permission: "result.release",
    // Issue #94: approved work waiting for final release lands here.
    badge: "notifications",
  },
  tests: { to: "/tests", label: "Tests & Results", permission: "test.assign" },
  inventory: { to: "/inventory", label: "Inventory", permission: "inventory.manage" },
  audit: { to: "/audit", label: "Audit Trail", permission: "audit.view" },
  ai: { to: "/ai", label: "AI Insights", feature: "ai" },
  team: { to: "/team", label: "Team", permission: "user.view" },
  organizations: { to: "/organizations", label: "Organizations", platformOnly: true },
  configuration: { to: "/configuration", label: "Configuration", permission: "catalogue.manage" },
  // Issue #94: released results and order decisions notify the client; the
  // badge marks both portal destinations because either page carries the
  // underlying content and acknowledges the notifications on open.
  portal: {
    to: "/portal",
    label: "Client Portal",
    permission: "portal.sample.view",
    badge: "notifications",
  },
  "portal-orders": {
    to: "/portal/orders",
    label: "Test Requests",
    permission: "portal.order.manage",
    badge: "notifications",
  },
};

const PERSONA_NAV_KEYS = {
  sample_manager: ["register-sample", "dashboard", "orders", "storage", "inventory"],
  analyst: ["worklist", "my-stats", "dashboard", "samples"],
  supervisor: ["supervisor-review", "schedule", "tests", "my-stats", "dashboard", "samples", "clients", "orders", "catalogue", "audit"],
  qa: ["qa-release", "dashboard", "samples", "audit", "team"],
  client_user: ["portal", "portal-orders"],
  system_admin: [
    "dashboard",
    "clients",
    "orders",
    "register-sample",
    "storage",
    "catalogue",
    "tests",
    "schedule",
    "my-stats",
    "inventory",
    "audit",
    "ai",
    "team",
    "configuration",
    "organizations",
  ],
};

const PERSONA_LANDING_PATHS = {
  sample_manager: "/samples?register=1",
  analyst: "/my-work",
  supervisor: "/review",
  qa: "/review",
  client_user: "/portal",
  system_admin: "/dashboard",
};

/**
 * Storage and inventory (S-10, issue #89).
 *
 * `PERSONA_NAV_KEYS` keeps these out of the primary workspace for everyone but
 * the Sample Manager, who administers them. The other internal personas still
 * need to *locate* a sample, so rather than losing the screens entirely they
 * get them in a secondary group: the right holding the `.view` grant without
 * the `.manage` grant is exactly the state that means "reads it, does not run
 * it". A client user holds neither and sees nothing.
 */
const DEMOTABLE = {
  storage: { view: "storage.view", manage: "storage.manage" },
  inventory: { view: "inventory.view", manage: "inventory.manage" },
};

const hasPermission = (user, permission) =>
  !permission || (Array.isArray(user?.permissions) && user.permissions.includes(permission));

const holds = (user, permission) =>
  Array.isArray(user?.permissions) && user.permissions.includes(permission);

/** A session holding no internal action is a client user (or has no role). */
export const isInternalUser = (user) => holds(user, "dashboard.view");

export function landingPathForUser(user) {
  if (!user) return "/login";
  const primaryRole = user.role || rolesForUser(user)[0];
  const mapped = PERSONA_LANDING_PATHS[primaryRole];
  if (mapped) return mapped;
  // An unmapped or legacy role still has to land somewhere it may open, so
  // fall back to what the server actually granted rather than assuming the
  // internal overview.
  if (holds(user, "portal.sample.view") && !isInternalUser(user)) return "/portal";
  if (holds(user, "result.review") || holds(user, "result.release")) return "/review";
  if (holds(user, "result.enter")) return "/my-work";
  if (holds(user, "sample.register")) return PERSONA_NAV_ITEMS["register-sample"].to;
  return "/dashboard";
}

const isVisible = (key, user, { aiEnabled, isPlatformAdmin }) => {
  const item = PERSONA_NAV_ITEMS[key];
  if (!item) return false;
  if (item.feature === "ai" && !aiEnabled) return false;
  if (item.platformOnly && !isPlatformAdmin) return false;
  return hasPermission(user, item.permission);
};

export function navigationKeysForUser(
  user,
  { aiEnabled = false, isPlatformAdmin = !!user?.is_platform_admin } = {},
) {
  // The per-persona order is deliberate — each list opens on the task that
  // persona signs in to do. A role absent from the table is not given an empty
  // sidebar; it falls back to every item the server permits, in canonical
  // order, so an unmapped or legacy role degrades to less curation rather than
  // to nothing at all.
  const assignedRoles = rolesForUser(user);
  const mappedRoles = assignedRoles.filter((role) => PERSONA_NAV_KEYS[role]);
  const keys = mappedRoles.length
    ? [...new Set(mappedRoles.flatMap((role) => PERSONA_NAV_KEYS[role]))]
    : Object.keys(PERSONA_NAV_ITEMS);
  return keys.filter((key) => isVisible(key, user, { aiEnabled, isPlatformAdmin }));
}

/**
 * The secondary group: screens this persona reads but does not administer.
 * Never overlaps `navigationKeysForUser` — an item promoted into the primary
 * workspace is not repeated here.
 */
export function referenceKeysForUser(
  user,
  { aiEnabled = false, isPlatformAdmin = !!user?.is_platform_admin } = {},
) {
  const primary = navigationKeysForUser(user, { aiEnabled, isPlatformAdmin });
  return Object.entries(DEMOTABLE)
    .filter(([key, grants]) =>
      !primary.includes(key) && holds(user, grants.view) && !holds(user, grants.manage))
    .map(([key]) => key);
}

export function hasRegistrationIntent(searchParams) {
  return searchParams?.get("register") === "1";
}
